// copied from https://github.com/ngxtension/ngxtension-platform/blob/main/libs/ngxtension/inject-lazy/src/inject-lazy.ts
import {
    DestroyRef,
    EnvironmentInjector,
    Injectable,
    Injector,
    type Provider,
    type ProviderToken,
    Type,
    createEnvironmentInjector,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { defer } from 'rxjs';

import { assertInjector } from './assert-injector';

/**
 * Lazy import type that includes default and normal imports
 */
export type LazyImportLoaderFn<T> = () => Promise<ProviderToken<T>>;

@Injectable({ providedIn: 'root' })
export class InjectLazyImpl<T> {
    private overrides = new WeakMap(); // no need to clean up
    override<T>(type: Type<T>, mock: Type<unknown>) {
        this.overrides.set(type, mock);
    }

    get(injector: Injector, loader: LazyImportLoaderFn<T>): Observable<T> {
        return defer(() =>
            loader().then((type) => {
                // Check if we have overrides, O(1), low overhead
                if (this.overrides.has(type)) {
                    const module = this.overrides.get(type);
                    return new module();
                }

                // If the service uses DestroyRef.onDestroy() it will never be called.
                // Even if injector is a NodeInjector, this works only with providedIn: root.
                // So it's the root injector that will provide the DestroyRef (and thus never call OnDestroy).
                // The solution would be to create an EnvironmentInjector that provides the class we just lazy-loaded.
                if (!(injector instanceof EnvironmentInjector)) {
                    // We're passing a node injector to the function

                    // This is the DestroyRef of the component
                    const destroyRef = injector.get(DestroyRef);

                    // This is the parent injector of the environmentInjector we're creating
                    const environmentInjector = injector.get(EnvironmentInjector);

                    // Creating an environment injector to destroy it afterward
                    const newInjector = createEnvironmentInjector([type as Provider], environmentInjector);

                    // Destroy the injector to trigger DestroyRef.onDestroy on our service
                    destroyRef.onDestroy(() => newInjector.destroy());

                    // We want to create the new instance of our service with our new injector
                    injector = newInjector;
                }

                return injector.get(type)!;
            }),
        );
    }
}

/**
 * Loads a service lazily. The service is loaded when the observable is subscribed to.
 *
 * @param loader A function that returns a promise of the service to load.
 * @param injector The injector to use to load the service. If not provided, the current injector is used.
 * @returns An observable of the service.
 *
 * @example
 * ```ts
 * const dataService$ = injectLazy(() => import('./data-service').then((m) => m.MyService));
 * or
 * const dataService$ = injectLazy(() => import('./data-service'));
 * ```
 */
export function injectLazy<T>(loader: LazyImportLoaderFn<T>, injector?: Injector): Observable<T> {
    injector = assertInjector(injectLazy, injector);
    const injectImpl = injector.get<InjectLazyImpl<T>>(InjectLazyImpl);
    return injectImpl.get(injector, loader);
}
